/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import convert from "heic-convert/browser";

import cn from "classnames";
import ReactCrop from "react-image-crop";
import { useOnClickOutside } from "usehooks-ts";

import openToast from "utils/openToast";

import { useUI } from "state";

import { SkeletonDiv } from "components/common";

import { DropZone } from "components/uploaders";

import "react-image-crop/dist/ReactCrop.css";
import uploadCloudinary from "../Cloudinary/uploadCloudinary";
import Modal from "components/ui/Modal";
import Stack from "components/ui/Stack";
import ImageViewer from "components/ui/ImageViewer";
import { Button } from "components/ui/Button";
import Loading from "components/ui/Loading";

const MIN_ALLOWED_SIZE = 120;

const HEIF_MIME_TYPES = [
  "image/heif",
  "image/heic",
  "image/heif-sequence",
  "image/heic-sequence",
];

const HEIF_EXTENSIONS = [".heif", ".heic"];

const isHeifFile = (file) => {
  if (HEIF_MIME_TYPES.includes(file.type?.toLowerCase())) return true;
  const name = file.name?.toLowerCase() || "";
  return HEIF_EXTENSIONS.some((ext) => name.endsWith(ext));
};

class ImageCropper {
  getCropFromFile = (divProps, crop) => {
    let newCrop;
    newCrop = this.#getCorrectCrop(crop, divProps);
    newCrop = this.#centerCrop(newCrop, divProps);

    return newCrop;
  };

  #getCorrectCrop = (crop, dimensions) => {
    const { width, height } = dimensions;
    // if the crop height is greater than the image height, set the crop height to the image height
    const newCrop = { ...crop };
    if (crop.height > height) {
      // reduce it a little bit
      newCrop.height = height - 5;
    }
    // if the crop width is greater than the image width, set the crop width to the image width
    if (crop.width > width) {
      newCrop.width = width - 5;
    }

    return newCrop;
  };

  #centerCrop = (crop, dimensions) => {
    const { width, height } = dimensions;
    const newCrop = { ...crop };
    if (crop.x > width - crop.width) {
      newCrop.x = width - crop.width;
    }

    if (crop.y > height - crop.height) {
      newCrop.y = height - crop.height;
    }

    return newCrop;
  };

  getCroppedImage = (image, crop) => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext("2d");
    // https://cloudinary.com/guides/automatic-image-cropping/cropping-images-in-javascript
    const opts = {
      image,
      sx: crop.x * scaleX,
      sy: crop.y * scaleY,
      sWidth: crop.width * scaleX,
      sHeight: crop.height * scaleY,
      x: 0,
      y: 0,
      toWidth: crop.width,
      toHeight: crop.height,
    };

    ctx.drawImage(...Object.values(opts));

    const base64Image = canvas.toDataURL("image/jpeg");
    return base64Image;
  };
}

const Cropper = ({ onUpload, folderName, width, height }) => {
  const CropperClassRef = useRef(new ImageCropper());
  if (!CropperClassRef.current) {
    CropperClassRef.current = new ImageCropper();
  }

  const { closeNestedModal } = useUI();

  const { getCroppedImage, getCropFromFile } = CropperClassRef.current;

  const [crop, setCrop] = useState({
    unit: "px",
    width: 450,
    height: 450,
    x: 73.80000305175781,
    y: 57.800018310546875,
    aspect: 1,
  });

  const ReactCropperRef = useRef(null);
  const [image, setImage] = useState("");
  const [blob, setBlob] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const modalRef = useRef(null);

  // For upload image to cloudinary:
  const uploadImg = async (image) => {
    await uploadCloudinary(
      [image],
      {
        onUploadStart: () => setLoading(true),
        onError: () => {
          setLoading(false);
          setHasError(true);
        },
        onUpload: (data) => {
          setLoading(false);
          onUpload(data[data.length - 1].img);
        },
        allowedFileTypes: ["image/*"],
        folderName,
        cloudinaryType: "image",
      },
      width,
      height,
    );
  };

  // For drag and drop:
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    const invalidFiles = rejectedFiles.map(
      (fileRejection) => fileRejection.file,
    );

    if (invalidFiles.length > 0) {
      openToast(`Invalid format(s). Only images are allowed.`, "error");

      return;
    }

    const file = acceptedFiles[0];

    // HEIF/HEIC images are not supported by browsers natively.
    // Convert them to JPEG so the preview and cropper work correctly.
    if (isHeifFile(file)) {
      try {
        setLoading(true);
        const arrayBuffer = await file.arrayBuffer();

        const outputBuffer = await convert({
          buffer: new Uint8Array(arrayBuffer),
          format: "JPEG",
          quality: 0.9,
        });

        const resultBlob = new Blob([outputBuffer], { type: "image/jpeg" });
        const url = URL.createObjectURL(resultBlob);
        setBlob(url);

        return;
      } catch (error) {
        console.error("HEIF conversion failed:", error);

        openToast(
          "Failed to process HEIF/HEIC image. Please try a different format.",
          "error",
        );

        return;
      } finally {
        setLoading(false);
      }
    }

    const url = URL.createObjectURL(file);
    setBlob(url);
  }, []);

  const [isReady, setReady] = useState(false);
  // to adjust crop handlers to fit image dimensions
  useLayoutEffect(() => {
    const adjustCrop = () => {
      if (ReactCropperRef.current) {
        const { width, height } = {
          width: ReactCropperRef.current.offsetWidth,
          height: ReactCropperRef.current.offsetHeight,
        };

        const newCrop = getCropFromFile({ width, height }, crop);
        setCrop(newCrop);
        setReady(true);
      }
    };

    if (blob && blob.length > 0) {
      adjustCrop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image]);

  useOnClickOutside(modalRef, () => {
    closeNestedModal();
  });

  return (
    <Modal.Container
      className={cn("!max-w-full !max-h-full", {
        "p-5": !blob.length,
      })}
      test-id="KbodRyeA"
      ref={modalRef}
    >
      <span test-id="zTyNj0vA">
        {hasError && "Failed to upload, please try again."}
      </span>

      <Stack test-id="E351ceYI">
        {blob.length > 0 ? (
          <Stack test-id="61C_xEPe">
            <Modal.Header title="Crop image below" test-id="C1c_LIRs" />

            <Modal.Body className="bg-cover max-h-[500px]" test-id="HhQdQA2o">
              <Stack className="bg-secondary-accent h-full" test-id="3LZwaAx3">
                <ReactCrop
                  crop={crop}
                  onDragStart={() => {
                    // disable any clicks outside of the modal
                    document.body.style.pointerEvents = "none";
                  }}
                  onDragEnd={() => {
                    // enable clicks outside of the modal
                    document.body.style.pointerEvents = "auto";
                  }}
                  onChange={(c) => {
                    if (
                      c.width < MIN_ALLOWED_SIZE ||
                      c.height < MIN_ALLOWED_SIZE
                    ) {
                      return setCrop({
                        ...crop,
                        width: MIN_ALLOWED_SIZE,
                        height: MIN_ALLOWED_SIZE,
                      });
                    }

                    setCrop(c);
                  }}
                  className={cn(isLoading && "hidden")}
                  aspect={1}
                  test-id="wPCwgc2Y"
                >
                  {/* Preview image */}

                  <div
                    ref={ReactCropperRef}
                    className={cn(
                      "w-[320px] sm:w-[460px] h-fit relative",
                      // !isReady && "hidden"
                    )}
                    test-id="G3Cm3juL"
                  >
                    {!isReady && (
                      <>
                        <div
                          className="bg-primary h-full w-full inset-0 absolute z-[1] pointer-events-none"
                          test-id="ydZ1rnqH"
                        />
                        <SkeletonDiv
                          className="h-full w-full inset-0 absolute z-[1] pointer-events-none"
                          test-id="55hzXlMO"
                        />
                      </>
                    )}

                    <ImageViewer
                      src={blob}
                      onLoad={(e) => setImage(e.target)}
                      className="w-full h-fit"
                      test-id="N-wR9XGL"
                    />
                  </div>
                </ReactCrop>

                {/* Loading */}

                <div
                  className={cn(
                    "w-[320px] sm:w-[460px] h-fit overflow-hidden ",
                    !isLoading && "hidden",
                  )}
                  test-id="JVq3IAd9"
                >
                  <ImageViewer
                    src={blob}
                    isLoading={isLoading}
                    test-id="COrqzOcE"
                  />
                </div>
              </Stack>
            </Modal.Body>

            <Modal.Footer className="flex space-x-3" test-id="4Ebp1Kbv">
              <Button
                variant="primary-outline"
                size="xl"
                onClick={() => setBlob("")}
                test-id="y_vJvoHz"
              >
                Cancel
              </Button>

              <Button
                variant="primary"
                size="xl"
                onClick={async () => {
                  const img = getCroppedImage(image, crop);
                  await uploadImg(img);
                }}
                disabled={crop.width === 0 || crop.height === 0}
                test-id="U0AMSPy-"
              >
                Accept
              </Button>
            </Modal.Footer>
          </Stack>
        ) : (
          <Stack test-id="ElqcqXIa" className="relative">
            <div
              className={cn("size-full absolute inset-0 z-10", {
                hidden: !isLoading,
              })}
            >
              <Loading className="size-full" test-id="hoftk44R" />
            </div>

            <DropZone
              onDrop={onDrop}
              acceptedFormats="image/*"
              test-id="v2dQWDmT"
            />
          </Stack>
        )}
      </Stack>
    </Modal.Container>
  );
};

export default Cropper;
